import React from 'react';
import Markdown from 'react-markdown';
import rehypeKatex from 'rehype-katex';
import remarkMath from 'remark-math';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import 'katex/dist/katex.min.css';

import Box from '@mui/material/Box';
import { alpha } from '@mui/material';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { palette } from '../../theme/palette';
import Iconify from '../../theme/iconify/iconify';

import Code from './Code';

function Message({ message }) {
    const isChatbot = message.role === 'CHATBOT';
    const alignment = isChatbot ? 'flex-start' : 'flex-end';
    const maxWidth = isChatbot ? '75%' : '60%';
    const background = isChatbot ? palette().background.neutral : '#1877F2';
    const color = isChatbot ? palette().text.primary : '#FFFFFF';

    const { t } = useTranslation();
    const navigate = useNavigate();
    const openReference = (reference) => {
        if (reference.url) {
            window.open(reference.url, '_blank', 'noopener,noreferrer');
            return;
        }

        if (reference.id) navigate(`/documents/${reference.id}`);
    };

    return (
        <>
            <Grid container sx={{ my: 2 }}>
                <Grid item xs={12}
                    sx={{
                        display: 'flex',
                        justifyContent: alignment
                    }}
                >
                    <Paper
                        sx={{
                            p: 1.5,
                            background: background,
                            color: color,
                            borderRadius: '8px',
                            maxWidth: {
                                xs: '100%',
                                md: maxWidth
                            }
                        }}
                    >
                        {isChatbot ?
                            <Markdown
                                className='mISLec_chat_message'
                                remarkPlugins={[remarkMath]}
                                rehypePlugins={[rehypeKatex]}
                                components={{
                                    code(props) {
                                        const { children, className, node, ...rest } = props;
                                        const match = /language-(\w+)/.exec(className || '');
                                        return match ? (
                                            <Code
                                                children={children}
                                                match={match}
                                                rest={rest}
                                            />
                                        ) : (
                                            <code
                                                {...rest}
                                                className={className}
                                                style={{
                                                    color: '#e83e8c',
                                                    background: 'transparent'
                                                }}
                                            >
                                                {children}
                                            </code>
                                        )
                                    }
                                }}
                            >
                                {message.text
                                    .replace(/\n\n-/g, '\n-')
                                    .replace(/\n\n(\d+)\./g, '\n$1.')}
                            </Markdown>
                            :
                            <div style={{ fontSize: '0.95rem', whiteSpace: 'pre-wrap' }}>
                                {message.text}
                            </div>
                        }
                    </Paper>
                </Grid>
                {message.references?.length > 0 && (
                    <Grid item xs={12}
                        sx={{
                            mt: 2,
                            maxWidth: {
                                xs: '100%',
                                md: maxWidth
                            }
                        }}
                    >
                        <Grid container>
                            <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center' }}>
                                <Iconify icon='mdi:link-variant' />
                                <Typography variant='h6' sx={{ display: 'inline', ml: 1, fontSize: '0.95rem !important' }}>{t('chat.message.source')}</Typography>
                            </Grid>
                            <Grid item xs={12} sx={{ mt: 1 }}>
                                <Grid container spacing={2}>
                                    {message.references.map(reference => (
                                        <Grid item xs={6} md={4} sx={{ mb: 1 }} key={reference.id || reference.url || reference.title}>
                                            <Tooltip title={reference.title} arrow>
                                                <Paper elevation={1} onClick={() => openReference(reference)}>
                                                    <Box
                                                        className='mISLec_chat_source'
                                                        sx={{
                                                            px: 2,
                                                            py: 1,
                                                            backgroundColor: 'white',
                                                            borderRadius: '16px',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        <Grid container spacing={1}>
                                                            <Grid item xs={12}>
                                                                <Link
                                                                    href={reference.url || undefined}
                                                                    target={reference.url ? '_blank' : undefined}
                                                                    rel={reference.url ? 'noopener noreferrer' : undefined}
                                                                    color='inherit'
                                                                    variant='caption'
                                                                    underline='none'
                                                                    onClick={(event) => {
                                                                        event.preventDefault();
                                                                        openReference(reference);
                                                                    }}
                                                                    sx={{
                                                                        fontWeight: '500',
                                                                        overflow: 'hidden',
                                                                        WebkitLineClamp: 1,
                                                                        display: '-webkit-box',
                                                                        WebkitBoxOrient: 'vertical',
                                                                    }}
                                                                >
                                                                    {reference.title}
                                                                </Link>
                                                            </Grid>
                                                            <Grid item xs={12}>
                                                                <Grid container sx={{
                                                                    p: 0.5,
                                                                    px: 0.75,
                                                                    width: 'fit-content',
                                                                    alignItems: 'center', borderRadius: '8px !important',
                                                                    backgroundColor: 'rgba(33, 43, 54, 0.06)'
                                                                }}>
                                                                    <Iconify icon={reference.url ? 'mdi:web' : 'ion:document'} sx={{ width: 16, height: 16, color: 'primary.main' }} />
                                                                    <Typography variant='caption' sx={{ color: 'text.disabled', ml: 0.5, fontSize: '0.725rem' }}>
                                                                        {reference.url ? t('chat.message.website') : t('chat.message.document')}
                                                                    </Typography>
                                                                </Grid>
                                                            </Grid>
                                                        </Grid>
                                                    </Box>
                                                </Paper>
                                            </Tooltip>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                )}
            </Grid>
        </>
    );
}

export default Message;
